import React, { useEffect, useState, useRef } from 'react';
import PageTitle from '../Components/PageTitle';
import AxiosApi from '../Helper/axios.api';
import { useSelector, useDispatch } from "react-redux";
import { Link, useParams } from "react-router-dom";
import moment from "moment";
import { useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";
import { logout } from "../Action/Auth.action";

function copyToClipboard(text, ev) {
  navigator.clipboard.writeText(text);

  // find sibling input
  let button = ev.target;
  let target = button.previousElementSibling;

  // change its value to "copied"
  target.value = "copied to clipboard!"

  // after 1s set it back to text
  setTimeout(function () {
    target.value = text
  }, 1000)
}

function diffYMDHMS(date1, date2) {
  const isFuture = date2.isBefore(date1);
  let [start, end] = isFuture ? [moment(date1), moment(date2)] : [moment(date2), moment(date1)];

  const years = start.diff(end, "years");
  end.add(years, "years");

  const months = start.diff(end, "months");
  end.add(months, "months");

  const days = start.diff(end, "days");
  end.add(days, "days");

  if (years > 0 || months > 0 || days > 1) {
    return <>on <b>{start.format("MMM DD, YYYY h:mm A")}</b></>;
  }

  if (days === 1) {
    return isFuture
      ? <><b>Tomorrow</b> at <b>{start.format("h:mm A")}</b></>
      : <><b>Yesterday</b> at <b>{start.format("h:mm A")}</b></>;
  }

  const hours = start.diff(end, "hours");
  end.add(hours, "hours");

  const minutes = start.diff(end, "minutes");
  end.add(minutes, "minutes");


  const parts = [];
  if (hours > 0) parts.push(<span key="h">{hours}h</span>);
  if (hours <= 5 && minutes > 0) parts.push(<span key="m">{parts && " "}{minutes}m</span>);

  if (parts.length === 0) {
    return isFuture ? <b>soon</b> : <b>now</b>;
  }
  return isFuture
    ? <>in <b>{parts}</b></>
    : <><b>{parts}</b> ago</>;
}

function openLink(url) {
  window.open(url, "_blank").focus();
}


function CalendarCard({ moment_start, moment_end }) {

  // FROM: https://stackoverflow.com/a/19867654
  const calendarUrl = new URL("https://calendar.google.com/calendar/r/eventedit");
  calendarUrl.searchParams.set("action", "TEMPLATE"); // Required
  calendarUrl.searchParams.set("dates", `${moment_start.format("YYYYMMDDTHHmmssZ")}/${moment_end.format("YYYYMMDDTHHmmssZ")}`);
  calendarUrl.searchParams.set("ctz", Intl.DateTimeFormat().resolvedOptions().timeZone);
  calendarUrl.searchParams.set("trp", "true"); // Set as "Busy" in Calendar
  calendarUrl.searchParams.set("text", "DeepTherapy Reservation");
  calendarUrl.searchParams.set("details", `View this reservation by navigating to <a href="${document.URL}">${document.URL}</a>`);

  return (
    <div className="calendar-card">
      <div className="calendar-date">
        <div className="calendar-weekday">{moment_start.format("ddd")}</div>
        <div className="calendar-day">{moment_start.format("D")}</div>
        <div className="calendar-month">{moment_start.format("MMMM")}</div>
      </div>
      <div className="calendar-time">
        <div className="fa fa-clock clock-icon"></div>
        <div className="calendar-time-range">
          {moment_start.format("h:mm A")}<br />{moment_end.format("h:mm A")}
        </div>
      </div>
      <div className="calendar-share">
        <div className="fa fa-calendar-plus calendar-icon"></div>
        <a target="_blank" href={calendarUrl.toString()}>Add to Calendar</a>
      </div>
    </div>
  );
}

export default function ReservationPortal() {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const [state, setState] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const createdSessionRef = useRef(false);
  let { reservation_id } = useParams();
  const navigate = useNavigate();

  const fetchState = async () => {
    try {
      let response;
      if (auth.userDeatils) {
        response = await AxiosApi.withToken(
          auth.accessToken,
          "reservations/" + reservation_id + "/session",
          "post",
          { user_id: auth.userDeatils.user_id }
        );
      } else {
        response = await AxiosApi.withoutToken(
          "reservations/" + reservation_id + "/session",
          "post"
        );
      }

      if (response.status) {
        let data = response.data;
        if (data) {
          data.now = moment(new Date()).local();
          data.moment_start = moment(data.time_start).local();
          data.moment_end = moment(data.time_end).local();
          data.moment_session_last_update = moment(
            data.session_last_update ? data.session_last_update : new Date()
          ).local();
          data.isOngoing = data.now.isAfter(moment(data.time_start).local());
          data.sessionIsReady =
            (data.session_state === "Ready" || data.session_state === "Started") &&
            data.isOngoing;
        }

      } else if (response.login === false) {
        dispatch(logout());
        navigate("/login");
      }
      setState((state) => response);
    } catch (error) {
      console.log(error);
    }
  };

  const createSession = () => {
    if (isLoading) return;
    setIsLoading(true);

    if (auth.userDeatils) {
      AxiosApi.withToken(
        auth.accessToken,
        "reservations/" + reservation_id + "/session/create",
        "post",
        { user_id: auth.userDeatils.user_id }
      )
      .then((response) => {
        if (!response.status) throw Error(response.message);
        fetchState();
      })
      .catch((error) => {
        toast.error(error.message, {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
    } else {
      console.error("Attempted to create session while not being logged in.");
    }
  };

  const createSessionWithConfirmation = () => {
    const userConfirmed = window.confirm(
      "Your session does not appear to have crashed. Recreating a session can cause startup delays. Are you sure you want to recreate it?"
    );
    if (userConfirmed) {
      createSession();
    }
  };

  useEffect(() => {
    const runFetch = async () => {
      await fetchState();
    };

    runFetch();
    const interval = setInterval(fetchState, 5000);
    return () => clearInterval(interval);
  }, []);

  // Automatically trigger "Create Session" if the session may start, but hasn't yet
  useEffect(() => {
    // Use localstorage, since StrictMode and rendering might trigger this effect many times
    // localstorage persists even between (re)mounting, so this is a better metric to test whether
    // this method is already triggered
    const hasTriggered = localStorage.getItem("createdSessionRef") === "true";
    createdSessionRef.current = hasTriggered;

    if (state.data?.isOngoing && state.data?.session_state === "" && !createdSessionRef.current) {
      localStorage.setItem("createdSessionRef", "true");
      createdSessionRef.current = true;
      createSession();
    }
  }, [state]);

  useEffect(() => {
    const timeout = setTimeout(() => {localStorage.setItem("createdSessionRef", "false");}, 5000);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <>
    <PageTitle title="DeepTherapy | Reservation" />
    <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60%'}}>
      <div>
        { ( isLoading || !state.status === undefined ) ? (
          <>
            <h1>Loading ...</h1>
            <div className="lds-ellipsis">
              <div style={{backgroundColor:"black"}}></div>
              <div style={{backgroundColor:"black"}}></div>
              <div style={{backgroundColor:"black"}}></div>
              <div style={{backgroundColor:"black"}}></div>
            </div>
          </>
        ) : !state.status ? (
          <p>Something went wrong: <b>{state.message}</b></p>
        ) : (
          <>
            <div style={{display: 'flex', gap: "10px", alignItems: 'flex-start', flexWrap: 'wrap', justifyContent:'center'}}>
              <CalendarCard moment_start={state.data.moment_start} moment_end={state.data.moment_end} />
              <div style={{maxWidth: "48rem"}}>
                <h1>{state.data.name}</h1>
                { state.data.isOngoing ? (
                  <>
                    { state.data.is_owner && ( // Display session control (ie (Re)Start buttons) to therapist
                      <>
                        { !state.data.session_state ? ( // No session set -> button to create one
                          <p>
                            <button className='btn btn-primary' onClick={createSession}>Click here to start the session</button>
                          </p>
                        ) : (state.data.session_state == 'Unkown' || state.data.session_state == 'Initializing') ? ( // Session is created but not ready -> Show a "Please wait"
                          <>
                            <p><b>Your session is being prepared, please wait.</b><br /><i>{state.data.session_state_detailed}</i></p>
                            <div className="lds-ellipsis" style={{marginLeft:"3rem"}}>
                              <div style={{backgroundColor:"black"}}></div>
                              <div style={{backgroundColor:"black"}}></div>
                              <div style={{backgroundColor:"black"}}></div>
                              <div style={{backgroundColor:"black"}}></div>
                            </div>
                            { state.data.now.isAfter( moment(state.data.moment_session_last_update).add(10, "minutes") ) && (
                              <>
                              <p>
                                <b>The server does not appear to be responding.</b><br />
                                You can try to recreate it by clicking the button below, or contact our team.<br />
                                The last update from the server was {diffYMDHMS(state.data.moment_session_last_update, state.data.now)}.
                              </p>
                              <p>
                                <button className='btn btn-primary' onClick={createSession}>Click here to recreate the session</button>
                              </p>
                              </>
                            )}
                          </>
                        ) : state.data.sessionIsReady ? ( // Session is all ready for the user!
                          <>
                            { state.data.now.isAfter( moment(state.data.moment_session_last_update).add(5, "minutes") ) && (
                              <>
                              <p>
                                <b>The server does not appear to be responding.</b><br />
                                If something is wrong, you can try to recreate it by clicking the button below, or contact our team.<br />
                                The last update from the server was {diffYMDHMS(state.data.moment_session_last_update, state.data.now)}.
                              </p>
                              <p>
                                <button className='btn btn-primary' onClick={createSession}>Click here to recreate the session</button>
                              </p>
                              </>
                            )}

                            <div className="form-group mt-3">
                              <label><b>Therapist:</b></label>
                              <div className="input-group">
                                <input className="form-control" type="text" value={ state.data.link_therapist } disabled={true} />
                                <button
                                  className="btn btn-primary" style={{width: "160px", textAlign: "left"}} type="button"
                                  onClick={() => openLink(state.data.link_therapist)}
                                >
                                  Open in browser
                                </button>
                              </div>
                            </div>
                          </>
                        ) : ( // Session state is not a ready state
                          <>
                            <p>
                              <b>Something appears to have gone wrong.</b><br />
                              You can try to recreate the session by clicking the button below.<br />
                              If the issue persists, please contact our team for support.
                            </p>
                            <p>
                              <button className='btn btn-primary' onClick={createSession}>Click here to recreate the session</button>
                            </p>
                          </>
                        )}
                      </>
                    )}
                    { state.data.sessionIsReady ? ( // Display client link
                      <>
                        <div className="form-group mt-3">
                          <label><b>Client:</b></label>
                          <div className="input-group">
                          <input className="form-control" type="text" value={ state.data.link_client } disabled={true} />
                          {state.data.is_owner ? (
                            <button
                              className="btn btn-primary" style={{width: "160px", textAlign: "left"}} type="button"
                              onClick={(ev) => copyToClipboard(state.data.link_client, ev)}
                            >
                              Copy to clipboard
                            </button>
                          ) : (
                            <button
                              className="btn btn-primary" style={{width: "160px", textAlign: "left"}} type="button"
                              onClick={() => openLink(state.data.link_client)}
                            >
                              Open in browser
                            </button>
                          )}
                          </div>
                        </div>


                        <div className='mt-4'>
                          <label><b>Instructions:</b></label>
                          <div className="p-2 rounded" style={{backgroundColor: "#e9ecef", border: "1px solid #ced4da"}}>

                            {state.data.is_owner ? (
                              <ol className="mb-0">
                                <li>
                                    Open your link in a browser (just by clicking).
                                </li>
                                <li>
                                    Copy the client link and share it via email or another messaging tool.
                                </li>
                              </ol>
                            ) : (
                              <>Open your link in a browser (just by clicking).</>
                            )}
                          </div>
                        </div>
                      </>
                    ) : !state.data.is_owner && (
                      <div className="p-2 mt-4" style={{backgroundColor: "#e1e1e1"}}>
                        <p>Please wait for the session to start.</p>
                      </div>
                    )}
                    { state.data.is_owner && (
                      <p className='mt-4' style={{fontSize:'.875rem', textAlign: 'right'}}>If you are experiencing issues, you can recreate the session by clicking <a href='#' onClick={createSessionWithConfirmation}>here</a>, or contact our team.</p>
                    )}
                  </>
                ) : (
                  <p>Once your session has started, you can come back to this page to join the meeting.</p>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
    </>
  );
}
